import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Recorder from "@/components/Recorder";
import { Link } from "react-router-dom";
import { FolderOpen, Clock, FileAudio, Inbox, Check, X, ArrowUpRight, Calendar, Flame, Brain, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, refresh } = useAuth();
  const [folders, setFolders] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [invites, setInvites] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [recall, setRecall] = useState(null);
  const [nextExam, setNextExam] = useState(null);

  const load = async () => {
    const [f, r, inv, tt, rv, pg] = await Promise.allSettled([
      api.get("/folders"),
      api.get("/recordings"),
      api.get("/invites"),
      api.get("/timetable"),
      api.get("/review/today", { params: { limit: 1 } }),
      api.get("/progress"),
    ]);
    if (f.status === "fulfilled") setFolders(f.value.data);
    if (r.status === "fulfilled") setRecordings(r.value.data);
    if (inv.status === "fulfilled") setInvites(inv.value.data);
    if (tt.status === "fulfilled") {
      setTimetable(tt.value.data.slots);
      setCurrentSubject(currentFromTimetable(tt.value.data.slots));
    }
    if (rv.status === "fulfilled") setRecall(rv.value.data);
    if (pg.status === "fulfilled") setNextExam(pg.value.data?.next_exam || null);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const i = setInterval(() => setCurrentSubject(currentFromTimetable(timetable)), 30000);
    return () => clearInterval(i);
  }, [timetable]);

  const respond = async (id, accept) => {
    try {
      await api.post("/invites/respond", { invite_id: id, accept });
      toast.success(accept ? "Joined group" : "Declined");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const limit = user?.plan === "pro" ? 9999 : user?.plan === "starter" ? 50 : 5;
  const used = user?.usage?.recordings || 0;
  const usagePct = Math.min(100, (used / (limit === 9999 ? 60 : limit)) * 100);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <header className="mb-10 fade-up">
        <div className="label-mono mb-3">/ dashboard</div>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">
              Hey {user?.display_name?.split(" ")[0]}.
            </h1>
            <p className="text-neutral-500 mt-3 text-[0.92rem]">
              {currentSubject
                ? <>Right now: <span className="text-white">{currentSubject}</span></>
                : "No class scheduled — you're off the clock."}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="card-panel px-4 py-2.5">
              <div className="label-mono text-[0.62rem] mb-0.5">this month</div>
              <div className="text-sm tabular">
                <span className="text-white">{used}</span>
                <span className="text-neutral-500"> / {limit === 9999 ? "∞" : limit} recordings</span>
              </div>
              <div className="mt-1.5 h-0.5 w-32 bg-white/[0.06] overflow-hidden">
                <div className="h-full bg-white/70" style={{ width: `${usagePct}%` }} />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* daily recall + exam countdown strip */}
      <div className="grid md:grid-cols-3 gap-3 mb-4 fade-up" style={{ animationDelay: "0.04s" }}>
        <Link to="/review" className="glass p-5 flex items-center gap-4 hover:border-white/20 transition-colors md:col-span-2" data-testid="recall-tile">
          <div className="w-10 h-10 border border-amber-500/30 bg-amber-500/[0.06] flex items-center justify-center rounded-[2px] shrink-0">
            <Flame size={17} className={recall?.streak ? "text-amber-400" : "text-neutral-500"} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[0.92rem] font-medium">
              {recall?.due_count ? `${recall.due_count} cards due today` : "Daily Recall"}
            </div>
            <div className="text-[0.76rem] text-neutral-500 mt-0.5">
              {recall?.total_cards
                ? `${recall.streak}-day streak · ${recall.reviewed_today} reviewed today`
                : "Generate flashcards from a lesson to start your streak"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {recall?.due_count > 0 && (
              <span className="text-[0.72rem] font-mono text-black bg-amber-400 px-2 py-1 rounded-[2px]">START</span>
            )}
            <ArrowUpRight size={14} className="text-neutral-600" />
          </div>
        </Link>
        <Link to="/progress" className="glass p-5 flex items-center gap-4 hover:border-white/20 transition-colors" data-testid="exam-tile">
          <div className="w-10 h-10 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center rounded-[2px] shrink-0">
            {nextExam ? <CalendarClock size={16} className="text-rose-400"/> : <Brain size={16} className="text-neutral-500"/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[0.92rem] font-medium truncate">
              {nextExam ? `${nextExam.days_left} days · ${nextExam.subject}` : "Predicted grades"}
            </div>
            <div className="text-[0.76rem] text-neutral-500 mt-0.5 truncate">
              {nextExam?.predicted_grade ? `on track for ${nextExam.predicted_grade}` : "add an exam date"}
            </div>
          </div>
          <ArrowUpRight size={14} className="text-neutral-600 shrink-0" />
        </Link>
      </div>

      {/* main capture + invites row */}
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="md:col-span-2 fade-up" style={{ animationDelay: "0.08s" }}>
          <Recorder onUploaded={() => { load(); refresh(); }} currentSubject={currentSubject} />
        </div>

        <div className="fade-up" style={{ animationDelay: "0.16s" }}>
          <div className="glass p-6 h-full flex flex-col">
            <div className="label-mono mb-4 flex items-center gap-2">
              <Inbox size={11} /> invites
              {invites.length > 0 && (
                <span className="ml-auto text-[9px] label-mono text-amber-500">{invites.length} pending</span>
              )}
            </div>
            {invites.length === 0 && (
              <div className="text-neutral-500 text-[0.86rem]">No pending invites.</div>
            )}
            <div className="space-y-2.5 flex-1">
              {invites.map((iv) => (
                <div key={iv.id} className="border border-white/[0.06] p-3 rounded-[2px] bg-white/[0.015]" data-testid={`invite-${iv.id}`}>
                  <div className="text-[0.82rem] leading-tight">
                    <span className="text-white">@{iv.inviter_username}</span>
                    <span className="text-neutral-500"> → </span>
                    <span className="text-white">{iv.group_name}</span>
                  </div>
                  <div className="mt-2.5 flex gap-1.5">
                    <button
                      onClick={() => respond(iv.id, true)}
                      className="flex-1 px-3 py-1.5 bg-white text-black text-[0.72rem] font-medium rounded-[2px] inline-flex items-center justify-center gap-1 hover:bg-neutral-200 transition-colors"
                      data-testid={`invite-accept-${iv.id}`}
                    >
                      <Check size={11} /> Accept
                    </button>
                    <button
                      onClick={() => respond(iv.id, false)}
                      className="flex-1 px-3 py-1.5 border border-white/10 text-[0.72rem] rounded-[2px] inline-flex items-center justify-center gap-1 hover:bg-white/[0.04] transition-colors"
                      data-testid={`invite-decline-${iv.id}`}
                    >
                      <X size={11} /> Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* folders */}
      <section className="mb-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="label-mono mb-2">/ subjects</div>
            <h2 className="font-display text-xl tracking-tight">Folders</h2>
          </div>
          {folders.length === 0 && (
            <Link to="/timetable" className="text-[0.8rem] text-neutral-400 hover:text-white transition-colors inline-flex items-center gap-1">
              <Calendar size={13} /> Set timetable <ArrowUpRight size={12} />
            </Link>
          )}
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {folders.length === 0 && (
            <div className="col-span-full text-neutral-500 text-[0.86rem] border border-dashed border-white/[0.06] p-6 text-center rounded-[2px]">
              Record your first lesson to create a folder.
            </div>
          )}
          {folders.map((f) => (
            <div
              key={f.subject}
              className="glass p-5 hover:border-white/20 transition-colors group cursor-default"
              data-testid={`folder-${f.subject}`}
            >
              <FolderOpen className="text-neutral-400 group-hover:text-amber-500 transition-colors mb-3" size={18} />
              <div className="font-medium text-[0.95rem] mb-1">{f.subject}</div>
              <div className="text-[0.72rem] text-neutral-500 tabular">
                {f.count} recording{f.count > 1 ? "s" : ""}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* recent */}
      <section>
        <div className="label-mono mb-2">/ recent</div>
        <h2 className="font-display text-xl tracking-tight mb-4">Recent recordings</h2>
        <div className="space-y-1.5">
          {recordings.length === 0 && (
            <div className="text-neutral-500 text-[0.86rem]">Nothing yet.</div>
          )}
          {recordings.slice(0, 10).map((r) => (
            <Link
              to={`/recording/${r.id}`}
              key={r.id}
              className="group card-panel p-4 flex items-center gap-4 hover:border-white/20 transition-colors"
              data-testid={`recording-${r.id}`}
            >
              <div className="w-9 h-9 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center shrink-0 rounded-[2px]">
                <FileAudio size={15} className="text-neutral-400 group-hover:text-white transition-colors" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate text-[0.9rem]">{r.subject}</div>
                <div className="text-[0.75rem] text-neutral-500 truncate">
                  {r.transcript?.slice(0, 90) || "…"}
                </div>
              </div>
              <div className="text-[0.72rem] text-neutral-500 flex items-center gap-1 tabular">
                <Clock size={11} /> {Math.floor((r.duration_seconds || 0) / 60)}m
              </div>
              <ArrowUpRight size={13} className="text-neutral-600 group-hover:text-white transition-colors" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

function currentFromTimetable(slots) {
  if (!slots || !slots.length) return null;
  const now = new Date();
  const day = (now.getDay() + 6) % 7;
  const minutes = now.getHours() * 60 + now.getMinutes();
  for (const s of slots) {
    if (s.day === day && s.start_minute <= minutes && minutes < s.end_minute) return s.subject;
  }
  return null;
}

export default Dashboard;
